/**
 * Custom hook to manage representative form state
 * Provides typed state management and change handlers with proper error clearing
 */

import { useState, useCallback } from "react";
import { NAME_REGEX } from "@funded-here-interface/common";
import { RepresentativeFormErrors } from "../utils/validation";

export interface RepresentativeFormState {
  repFirstName: string;
  repLastName: string;
  repEmail: string;
  representative_title: string;
  adminRole: string;
}

/**
 * Hook to manage representative form state and field updates
 * Eliminates direct error object mutation by using proper setState
 */
export const useRepresentativeFormState = (
  initialState: RepresentativeFormState
) => {
  const [formState, setFormState] =
    useState<RepresentativeFormState>(initialState);
  const [errors, setErrors] = useState<RepresentativeFormErrors>({
    repFirstName: "",
    repLastName: "",
    repTitle: "",
    repEmail: "",
    adminRole: "",
  });

  /**
   * Clear error for a specific field
   */
  const clearError = useCallback((field: string) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  /**
   * Clear all errors for representative fields
   */
  const clearRepresentativeErrors = useCallback(() => {
    setErrors((prev) => ({
      ...prev,
      repFirstName: "",
      repLastName: "",
      repEmail: "",
      repTitle: "",
    }));
  }, []);

  /**
   * Set all errors at once (for validation)
   */
  const setAllErrors = useCallback((newErrors: RepresentativeFormErrors) => {
    setErrors(newErrors);
  }, []);

  /**
   * Update a single form field and clear its error
   */
  const updateField = useCallback(
    (field: keyof RepresentativeFormState, value: string) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
      // Map form fields to error fields
      const errorFieldMap: Record<keyof RepresentativeFormState, string> = {
        repFirstName: "repFirstName",
        repLastName: "repLastName",
        repEmail: "repEmail",
        representative_title: "repTitle",
        adminRole: "adminRole",
      };
      clearError(errorFieldMap[field]);
    },
    [clearError]
  );

  /**
   * Update multiple fields at once (batch update)
   */
  const updateFields = useCallback(
    (updates: Partial<RepresentativeFormState>) => {
      setFormState((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  /**
   * Create change handler for text fields
   */
  const createChangeHandler = useCallback(
    (field: keyof RepresentativeFormState) => (value: string) => {
      updateField(field, value);
    },
    [updateField]
  );

  /**
   * Create change handler for name fields (filters out non-name characters)
   */
  const createNameChangeHandler = useCallback(
    (field: keyof RepresentativeFormState) => (value: string) => {
      const cleanedValue = value.replace(NAME_REGEX, "");
      updateField(field, cleanedValue);
    },
    [updateField]
  );

  return {
    formState,
    errors,
    setAllErrors,
    clearError,
    clearRepresentativeErrors,
    updateField,
    updateFields,
    createChangeHandler,
    createNameChangeHandler,
  };
};
